import React from 'react';
import { 
  AGREED, 
  ALL, 
  ASSESSED, 
  BPA, 
  DISAGREED, 
  PROGRAM, 
  PROGRAM_DEPARTMENT, 
  PROGRAM_DIVISION, 
  UNASSESSED 
} from '../../static/keyWords.js';
import { 
  SCORE_TYPE_NAME_DEPARTMENT_ID, 
  SCORE_TYPE_NAME_FINAL_ID, 
  SCORE_TYPE_NAME_PEER_ID, 
  TABLE_COLUMN_WIDTH
} from '../../static/constants.js';
import { 
  filterByDepartment, 
  filterByDivision 
} from '../DepartmentScoring/departmentScoringHelpers.js';
import { 
  filterByBPAScore, 
  filterByResultScore 
} from '../../services/programService.js';
import { FILTER_TYPES } from '../reusable/Filters/filterHelpers.js';
import { ForgeButton, ForgeIcon, ForgeOption } from '@tylertech/forge-react';
import { handleKeyDown, splitByDelimiter, trimValue } from '../reusable/reusableFunctions.js';
import { REDUCER_ACTION_TYPES } from '../../store/Reducer.js';

export const CDN_EMPTY_STATE_IMAGE = 'https://cdn.forge.tylertech.com/v1/images/spot-hero/404-error-spot-hero.svg';

export const STEPS = [
  {label: 'Evaluation', value: 0 },
  {label: 'Team reconciliation', value: 1 }, 
  {label: 'Department reconciliation', value: 2 },
];

export const EVALUATION_PAGINATION_PAGE_SIZE_SELECTOR_VALUES = [5, 10, 20, 50, 100];
export const EVALUATION_DEFAULT_PAGINATION_PAGE_SIZE = 5;

export const RESULTS_STYLE = {
  breakText: {
    whiteSpace: 'pre-line'
  },
  autocompleteStack: {
    padding: '50px 15px'
  },
  fullWidth: {
    width: '100%'
  },
  cursor: {
    cursor: 'pointer',
    marginBottom: '0px',
  },
  icon: {
    position: 'relative',
    top: '7px'
  }
};

export const getDefaultColumns = (showColumns, nameConventions) => {
  return [
    { 
      headerName: 'Service type', 
      field: 'serviceType.name', 
      editable: false,
      context: 'Service Type',
      hide: !showColumns?.find((column) => column.name === 'Service Type')?.show,
      width: TABLE_COLUMN_WIDTH.MD
    },
    {
      headerName: nameConventions[PROGRAM_DEPARTMENT].alias || PROGRAM_DEPARTMENT,
      field: 'organizationLevels',
      editable: false,
      context: PROGRAM_DEPARTMENT,
      suppressColumnsToolPanel: false,
      width: TABLE_COLUMN_WIDTH.MD,
      hide: !showColumns?.find((column) => column.name === PROGRAM_DEPARTMENT)?.show,
      // organizationLevels: "department -- division"
      valueGetter: (params) => splitByDelimiter(params.data.organizationLevels)
    },
    {
      headerName: nameConventions[PROGRAM_DIVISION].alias || PROGRAM_DIVISION,      
      field: 'organizationLevels',
      editable: false,
      context: PROGRAM_DIVISION,
      suppressColumnsToolPanel: false,
      width: TABLE_COLUMN_WIDTH.MD,
      hide: !showColumns?.find((column) => column.name === PROGRAM_DIVISION)?.show,
      valueGetter: (params) => splitByDelimiter(params.data.organizationLevels, '--', 1)
    },
    { 
      headerName: nameConventions[PROGRAM].alias || PROGRAM,
      field: 'name', 
      editable: false,
      context: PROGRAM,
      hide: !showColumns?.find((column) => column.name === PROGRAM)?.show,
      width: TABLE_COLUMN_WIDTH.XXL
    },
    // uncomment if the release from team A contains Program Description inside the show columns
    // { 
    //   headerName: 'Program description', 
    //   field: 'description', 
    //   flex: 3 , 
    //   editable: false,
    //   context: 'Program Description',
    //   hide: !showColumns?.find((column) => column.name === 'Program Description')?.show,
    // },
    { 
      headerName: 'Actions', 
      cellRenderer: 'agGroupCellRenderer', 
      cellClass: 'grid-cell-centered-action',
      filter: false, 
      sortable: false, 
      width: TABLE_COLUMN_WIDTH.XS,
      pinned: 'right',
      suppressColumnsToolPanel: true,
      resizable: false,
      editable: false,
      cellStyle: {display: 'flex', justifyContent: 'center', alignItems: 'center'},
      context: 'Actions',
      tooltipValueGetter: (params) => {
        if (!params.node.group && !params.node.master) return null; 
        return params.node.expanded ? 'Close' : 'Open';
      }
    },
  ];
};

export const appendProgramsToResult = (result, programs, user) => {

  const sourcePrograms = result.resultType === BPA 
    ? programs 
    : programs.filter(program => 
      (program.serviceType && program.serviceType.name === result.resultType) 
      //|| (program.resultTypes && program.resultTypes.some(rt => rt.name === result.resultType))
    );

  const deptScoreMap = new Map(
    (result.departmentScores || []).map(score => [score.programId, score])
  );
  
  const peerScoreMap = new Map(
    (result.peerScores || [])
      .filter(score => score.lastUser === user.email)
      .map(score => [score.programId, score])
  );

  const processedPrograms = sourcePrograms.map(program => {
    const deptScore = deptScoreMap.get(program.id);
    const peerScore = peerScoreMap.get(program.id);
      
    return {
      name: program.name,
      id: program.id,
      description: program.description,
      serviceType: program.serviceType,
      resultTypes: program.resultTypes,
      scoreOptions: result.scoreOptions,
      isDirty: false,
      departmentScore: deptScore ? {
        name: deptScore.scoreOption.name,
        comment: deptScore.comments
      } : undefined,
      peerScore: peerScore ? peerScore.scoreOption.name : '',
      originalPeerScore: peerScore ? (peerScore.originalPeerScore || peerScore.originalPeerScore === '') ? peerScore.originalPeerScore :  peerScore.scoreOption.name : '',
      peerComment: peerScore ? peerScore.comments : '',
      scoreId: peerScore ? peerScore.id : undefined,
      agreed: (peerScore && (peerScore?.scoreOption?.name === deptScore?.scoreOption?.name)) ? true : false,
    };
  });

  return {
    ...result,
    programs: processedPrograms
  };
};

export const headerButtons = (setOpenFilters, activeStep) => {
  return [
    {
      variant: 'primary',
      onClickFunction: () => activeStep !== null ? setOpenFilters(true) : null,
      id: 0,
      name: (
        <>
          <ForgeIcon external externalType="all"name="filter_variant" /> Filter
        </>
      ),
      filterBadge: true,
    }
  ];
};

export const handleClickCancelButton = (state, dispatch) => {
  if (state.fireUnsavedChangesDialog) dispatch({ type: REDUCER_ACTION_TYPES.SET_USER_NAVIGATED, payload: !state.userNavigated });
};

export const handleGoBack = (state, dispatch, setCurrentResult, setCardSelected) => {
  if (state.fireUnsavedChangesDialog) {
    dispatch({ type: REDUCER_ACTION_TYPES.SET_USER_NAVIGATED, payload: !state.userNavigated });
  } else {
    dispatch({ type: REDUCER_ACTION_TYPES.BACK_BUTTON_FIRED, payload: false });
    setCurrentResult(null);
    setCardSelected(null);
  }
};

/************************************************************************ */
/*                         FILTERING STARTS HERE                          */
/************************************************************************ */


// -------------------- Evaluation Step Filtering Start ---------------------------- //
export const filterByDepartmentScore = (_programs, selectedDepartmentScores) => {
  if (!selectedDepartmentScores || selectedDepartmentScores.length === 0) {
    return _programs;
  }
  const departmentScores = selectedDepartmentScores.filter(score => score.type === FILTER_TYPES.BPA || score.type === FILTER_TYPES.RESULT);
  if (!departmentScores || departmentScores.length === 0) {
    return _programs;
  }
  let filteredPrograms = [..._programs];
  for (const departmentScore of departmentScores) {
    switch (departmentScore.type) { 
      case FILTER_TYPES.BPA:
        filteredPrograms = filteredPrograms.filter(program => {
          return filterByBPAScore(departmentScore.options, program, [SCORE_TYPE_NAME_DEPARTMENT_ID]);
        });
        break;
      case FILTER_TYPES.RESULT:
        filteredPrograms = filteredPrograms.filter(program => {
          return filterByResultScore(departmentScore.options, program);
        });
        break;
    }
  }
  return filteredPrograms;
};

export const handleEvaluationStepFiltering = (appliedFilters, programs) => {
  let filteredByDepartmentScore = filterByDepartmentScore(programs, appliedFilters.filter(filter => filter.type === FILTER_TYPES.BPA || filter.type === FILTER_TYPES.RESULT));
  let filteredByDepartment = filterByDepartment(filteredByDepartmentScore, appliedFilters.find(filter => filter.type === FILTER_TYPES.DEPARTMENT));
  let filteredByDivision = filterByDivision(filteredByDepartment, appliedFilters.find(filter => filter.type === FILTER_TYPES.DIVISION));
  return filteredByDivision;
};
// -------------------- Evaluation Step Filtering End ---------------------------- //

// ----------------------------------------------------------------------------//

// -------------------- Evaluation Tab Filtering Start ---------------------------- //
const filterByAgreement = (filterValue, unfilteredPrograms) => {
  let filteredPrograms;
  if (filterValue === AGREED) {
    filteredPrograms = unfilteredPrograms.filter(program => program?.originalPeerScore && program?.agreed);
  } else if (filterValue === DISAGREED) {
    filteredPrograms = unfilteredPrograms.filter(program => program?.originalPeerScore && program?.agreed === false);
  }
  return filteredPrograms;
};

const filterByStatus = (filterValue, unfilteredPrograms) => {
  let filteredPrograms = unfilteredPrograms.filter(program => {
    if (filterValue === ASSESSED) return !program.score && program.originalPeerScore && program.originalPeerScore.length > 0;
    if (filterValue === UNASSESSED) return !program.originalPeerScore || (program.originalPeerScore && program.score);
  });
  return filteredPrograms;
};

const filterByKeyword = (keyword, unfilteredPrograms) => {
  return unfilteredPrograms.filter(program =>
    program.name.toLowerCase().includes(keyword.toLowerCase()) ||
    (program.description && program.description.toLowerCase().includes(keyword.toLowerCase()))
  );
};

export const updateEvaluationProgram = (program, type, value, assessment) => {
  const updated = { ...program, isDirty: true };
  if (type === 'assessment') {
    updated.agreed = assessment;
    updated.peerScore = assessment ? value : '';
  } else if (type === 'peerReview') {
    updated.agreed = value === program.departmentScore?.name;
    updated.peerScore = value;
  } else if (type === 'comment') {
    updated.peerComment = value;
  }
  if (!updated.peerScore) updated.peerComment = '';
  return updated;
};

export const handleEvaluationTabFiltering = (currentResult, agreementFilterValue, statusFilterValue, searchKeyword, setFilteredPrograms) => {
  let programs = currentResult.programs ? [...currentResult.programs] : [];

  if (agreementFilterValue === ALL && statusFilterValue === ALL) {
    programs = currentResult.programs ? [...currentResult.programs] : [];
  } else if (agreementFilterValue === ALL && statusFilterValue !== ALL) {
    programs = filterByStatus(statusFilterValue, programs);
  } else if (agreementFilterValue !== ALL && statusFilterValue === ALL) {
    programs = filterByAgreement(agreementFilterValue, programs);
  } else {
    let tempPrograms = filterByAgreement(agreementFilterValue, programs);
    programs = filterByStatus(statusFilterValue, tempPrograms);
  }

  if (searchKeyword && searchKeyword.trim().length > 0) {
    programs = filterByKeyword(searchKeyword, programs);
  }

  setFilteredPrograms?.(programs);
  return programs;
  //goToPage(0, pageSize, programs);
};
// -------------------- Evaluation Tab Filtering End ---------------------------- //

// -------------------- Reconciliation Filtering Start ---------------------------- //

export const handleReconciliationFiltering = (appliedFilters, programs, setFilteredPrograms, setTotalCount) => {
  const filteredPrograms = programs.filter((program) => {
    let programDepartment = program.organizationLevels.split('--')[0];
    let programDivision = program.organizationLevels.split('--')[1];
    let isDepartmentCorrect = true;
    let isDivisionCorrect = true;
    let isBPAScoreCorrect = true;
    let isResultScoreCorrect = true;
    let isServiceTypeCorrect = true;
    const filterScoreTypes = [
      SCORE_TYPE_NAME_DEPARTMENT_ID,
      SCORE_TYPE_NAME_PEER_ID,
      SCORE_TYPE_NAME_FINAL_ID,
    ];
    for (const appliedFilter of appliedFilters) {
      switch (appliedFilter.type) {
        case FILTER_TYPES.DEPARTMENT:
          isDepartmentCorrect = appliedFilter.value.find(
            (department) => department.name === programDepartment
          );
          break;
        case FILTER_TYPES.DIVISION:
          isDivisionCorrect = appliedFilter.value.find(
            (division) => division.name === programDivision
          );
          break;
        case FILTER_TYPES.BPA:
          isBPAScoreCorrect = filterByBPAScore(
            appliedFilter.options,
            program,
            filterScoreTypes
          );
          break;
        case FILTER_TYPES.RESULT:
          isResultScoreCorrect = filterByResultScore(
            appliedFilter.options,
            program,
            filterScoreTypes
          );
          break;
        case FILTER_TYPES.SERVICE_TYPE:
          if (!program.serviceType) {
            isServiceTypeCorrect = false;
          } else {
            let serviceTypeName = program.serviceType.name
              ? program.serviceType.name
              : program.serviceType;
            isServiceTypeCorrect = appliedFilter.value.find(
              (selectedServiceType) =>
                selectedServiceType.name === serviceTypeName
            );
          }
          break;
      }
    }
    return (
      isDepartmentCorrect &&
      isDivisionCorrect &&
      isBPAScoreCorrect &&
      isResultScoreCorrect &&
      isServiceTypeCorrect
    );
  });

  setFilteredPrograms([...filteredPrograms]);
  setTotalCount(filteredPrograms.length);
};

// -------------------- Reconciliation Filtering End ---------------------------- //

/************************************************************************ */
/*                         FILTERING ENDS HERE                            */
/************************************************************************ */

export const mapEvaluationsForSaving = (id, evaluatedPrograms, currentResult) => {
  const programsWithoutScores = [];
  const evaluationsToSave = [];
  const programsToSave = [...evaluatedPrograms];

  programsToSave.filter(p=>p.isDirty).map((evaluatedProgram) => {
    const scoreOption = evaluatedProgram.scoreOptions.find((scoreOption) => scoreOption.name === evaluatedProgram.peerScore);
    if (!scoreOption) {
      programsWithoutScores.push({name: evaluatedProgram.name, id: evaluatedProgram.id});
      return;
    }
    const peerReviewComment = evaluatedProgram.peerComment;
    const scoreId = evaluatedProgram.scoreId ? evaluatedProgram.scoreId : null;
    const evaluation = {
      resultId: id,
      scoreTypeName: 'Peer',
      programId: evaluatedProgram.id,
      scoreOptionId: scoreOption.id,
      comments: peerReviewComment ? peerReviewComment: '',
      id: scoreId,
      scoreTypeId: SCORE_TYPE_NAME_PEER_ID,
      value: scoreOption.weight * currentResult.weight
    };
    evaluationsToSave.push(evaluation);
  });
  return { evaluationsToSave, programsWithoutScores };
};

export const mapTeamScoreForSaving = (teamScore, resultId, programId, teamScoreComment) => {
  const teamScoreInfo = {...teamScore};
  teamScoreInfo.resultId = resultId;
  teamScoreInfo.scoreTypeName = 'Final';
  teamScoreInfo.programId = programId;
  teamScoreInfo.scoreOptionId = Number(teamScore.scoreOptionId);
  teamScoreInfo.comments = trimValue(teamScoreComment);
  teamScoreInfo.isAutomaticallyGenerated = false;
  teamScoreInfo.scoreTypeId = SCORE_TYPE_NAME_FINAL_ID;
  teamScoreInfo.value = teamScore.value;
  return teamScoreInfo;
};

export const distributeTeamResultInfoData = (resultInfo, setPeerScores, setDepartmentScore, setTeamScore, setTeamScoreComment) => {
  const userScores = [];
  for (const userScore in resultInfo.usersByScoreOption) {
    let singleScore = {
      scoreOption: userScore.split('---')[1],
      scoresAmount: resultInfo.usersByScoreOption[userScore].length,
      scores: resultInfo.usersByScoreOption[userScore]
    };
    for (let i =0; i < resultInfo.programs.scores.length; i++) {
      if (singleScore.scoreOption == resultInfo.programs.scores[i].scoreOption.name) {
        singleScore.weight = resultInfo.programs.scores[i].scoreOption.weight;
      }
    }

    singleScore.scores.forEach(score => score.comment = score.comments);

    userScores.push(singleScore);
  }
  setPeerScores(userScores);

  const deptScore = resultInfo.programs.scores.find(score => score.scoreTypeId === SCORE_TYPE_NAME_DEPARTMENT_ID);
  setDepartmentScore(deptScore);

  const finalScore = resultInfo.programs.scores.find(score => score.scoreTypeId === SCORE_TYPE_NAME_FINAL_ID);
  setTeamScore(finalScore);
  setTeamScoreComment(finalScore?.comments || '');
};

export const distributeDepartmentResultInfoData = (resultInfo, setDepartmentScore, setPeerScores, setFinalScore, setTeamScore, setTeamScoreComment) => {
  const deptScore = resultInfo.scores.find(score => score.scoreTypeId === SCORE_TYPE_NAME_DEPARTMENT_ID);
  if (deptScore) {
    const correspondingScoreOption = resultInfo.scoreOptions.find(scoreOption => scoreOption.id === deptScore.scoreOptionId);
    deptScore.scoreOption = correspondingScoreOption ? correspondingScoreOption.name : 'N/A';
    deptScore.comment = deptScore.comments;
    setDepartmentScore(deptScore);
  }

  const peerScore = resultInfo.scores.find(score => score.scoreTypeId === SCORE_TYPE_NAME_PEER_ID);
  if (peerScore) {
    const correspondingScoreOption = resultInfo.scoreOptions.find(scoreOption => scoreOption.id === peerScore.scoreOptionId);
    peerScore.scoreOption = correspondingScoreOption ? correspondingScoreOption.name : 'N/A';
    peerScore.comment = peerScore.comments;
    setPeerScores(peerScore);
  }
    
  const finalScore = resultInfo.scores.find(score => score.scoreTypeId === SCORE_TYPE_NAME_FINAL_ID);
  if (finalScore) {
    const correspondingScoreOption = resultInfo.scoreOptions.find(scoreOption => scoreOption.id === finalScore.scoreOptionId);
    finalScore.scoreOption = correspondingScoreOption ? correspondingScoreOption.name : 'N/A';
    finalScore.comment = finalScore.comments;
    setFinalScore(finalScore);
    setTeamScore(finalScore);
    setTeamScoreComment(finalScore?.comments || '');
  } else {
    setTeamScore({});
    setTeamScoreComment('');
  }
};

export const handleTextView = (text, fullText, setFullText) => {
  if (!fullText && text.length > 120) {
    return (<>
      {text.slice(0, 120) + '... '}
      <ForgeButton 
        variant={'text'}
        className="text-with-more-less" 
        onClick={() => setFullText(true)}
        onKeyDown={(e) => handleKeyDown(e, () => setFullText(true))}
      >
        <span>View more</span> <ForgeIcon slot="end" external externalType="all" name="chevron_down"/>
      </ForgeButton>
    </>);
  } else if (fullText && text.length > 120) {
    return (<>
      {text + ' '}
      <ForgeButton
        variant={'text'}
        className="text-with-more-less" 
        //tabIndex="0"
        //className="text-with-more-less"
        onClick={() => setFullText(false)}
        onKeyDown={(e) => handleKeyDown(e, () => setFullText(false))}
      >
        <span>View less</span> <ForgeIcon slot="end" external externalType="all" name="chevron_up"/>
      </ForgeButton>
    </>);
  }
  return text;
};

export const PROGRAM_FILTER_TOOLBAR_STYLES = {
  fontWeight: '400',
  fontSize: '12px',
  theme: 'primary',
};

export const getAgreementFilterOptions = () => {
  return [ALL, AGREED, DISAGREED].map((x) => {
    return { label: x, value: x };
  }).map((option) => (
    <ForgeOption key={option.label} value={option.label}>
      {option.label}
    </ForgeOption>
  ));
};

export const getStatusFilterOptions = () => {
  return [ALL, ASSESSED, UNASSESSED].map((x) => {
    return { label: x, value: x };
  }).map((option) => (
    <ForgeOption key={option.label} value={option.label}>
      {option.label}
    </ForgeOption>
  ));
};

export const dynamicPeerScoreProgressCalculator = (programs) => {
  if (programs.length === 0) {
    return 0;
  }
  return (
    (programs.filter((program) => program.peerScore).length /
      programs.length) *
    100
  );
};

export const getDepartmentScore = (resultInfo, departmentReconciliation) => {
  if (departmentReconciliation) {
    return resultInfo.scores.find(score => score.scoreTypeId === SCORE_TYPE_NAME_DEPARTMENT_ID);
  }
  return resultInfo.programs.scores.find(score => score.scoreTypeId === SCORE_TYPE_NAME_DEPARTMENT_ID);
};

export const getPeerScore = (resultInfo, departmentReconciliation) => {
  if (departmentReconciliation) {
    return resultInfo.scores.find(score => score.scoreTypeId === SCORE_TYPE_NAME_PEER_ID);
  }
  return resultInfo.programs.scores.find(score => score.scoreTypeId === SCORE_TYPE_NAME_PEER_ID);
};

export const getPeerScores = (resultInfo) => {
  const userScores = [];
  for (const userScore in resultInfo.usersByScoreOption) {
    let singleScore = {
      scoreOption: userScore.split('---')[1],
      scoresAmount: resultInfo.usersByScoreOption[userScore].length,
      scores: resultInfo.usersByScoreOption[userScore]
    };
    for (let i =0; i < resultInfo.programs.scores.length; i++) {
      if (singleScore.scoreOption == resultInfo?.programs?.scores[i]?.scoreOption?.name) {
        singleScore.weight = resultInfo.programs.scores[i].scoreOption.weight;
      }
    }

    singleScore.scores.forEach(score => score.comment = score.comments);

    userScores.push(singleScore);
  }

  return userScores;
};

export const getTeamScore = (resultInfo, departmentReconciliation) => {
  if (departmentReconciliation) {
    return resultInfo.scores.find(score => score.scoreTypeId === SCORE_TYPE_NAME_FINAL_ID);
  }
  return resultInfo.programs.scores.find(score => score.scoreTypeId === SCORE_TYPE_NAME_FINAL_ID);
};

export const attachNewTeamScoreToResultsInfo = (resultsInfo, resultInfo, option) => {
  const updatedResultsMap = new Map(resultsInfo);
  const targetResult = updatedResultsMap
    .get(resultInfo.programs.id)
    ?.find(r => r.id === resultInfo.id);

  if (targetResult) {
    const result = resultInfo.programs.scores[0].result;
    const chosenOption = result.scoreOptions.find(so => so.id === Number(option.id));
    const value = chosenOption.weight * result.weight;

    updateOrAddScore(targetResult.programs.scores, option, value);
  }

  return updatedResultsMap;
};

export const attachNewTeamScoreToResultsInfoForDepartmentReconciliation = (resultsInfo, resultInfo, option, reconciliationPrograms) => {
  const updatedResultsInfo = new Map(resultsInfo);
  const targetResult = updatedResultsInfo.get(resultInfo.programId)?.find(r => r.id === resultInfo.id);

  if (!targetResult) return { updatedResultsInfo, _reconciliationPrograms: reconciliationPrograms };

  const value = (option.weight || 0) * (targetResult.weight || 0); // trust no one these days...
  updateOrAddScore(targetResult.scores, option, value);

  const _reconciliationPrograms = structuredClone(reconciliationPrograms);
  const targetProgram = _reconciliationPrograms.find(p => p.id === resultInfo.programId);
  const targetProgramResult = targetProgram.resultTypes
    .find(rt => rt.id === resultInfo.resultTypeId)
    .results.find(r => r.id === resultInfo.id);
  Object.assign(targetProgramResult, targetResult);
  return { updatedResultsInfo, _reconciliationPrograms };
};

const updateOrAddScore = (scores, option, value = 0) => {
  const currentScore = scores.find(s => s.scoreTypeId === SCORE_TYPE_NAME_FINAL_ID);

  if (currentScore) {
    Object.assign(currentScore, {
      scoreOption: option,
      scoreOptionId: option.id,
      isDirty: true,
      value
    });
  } else {
    scores.push({ //need to check this to attach budget id or more info if the score is new, but this after all changes on backend side and budget filtration for scores
      scoreOptionId: option.id,
      scoreTypeId: SCORE_TYPE_NAME_FINAL_ID,
      name: option.name,
      scoreOption: option,
      isDirty: true,
      value
    });
  }
};

export const attachNewCommentToResultsInfo = (resultsInfo, resultInfo, comment) => {
  const _resultsInfo = new Map(resultsInfo);
  const currentResult = _resultsInfo.get(resultInfo.programs.id).find(result => result.id === resultInfo.id);
  const currentScore = currentResult.programs.scores.find(score => score.scoreTypeId === SCORE_TYPE_NAME_FINAL_ID);
  currentScore.comments = comment;
  currentScore.isDirty = true;
  return _resultsInfo;
};

export const attachNewCommentToResultsInfoForDepartmentReconciliation = (resultsInfo, resultInfo, comment, reconciliationPrograms) => {
  const updatedResultsInfo = new Map(resultsInfo);
  const targetResult = updatedResultsInfo.get(resultInfo.programId).find(r => r.id === resultInfo.id);

  if (targetResult) {
    const currentScore = targetResult.scores.find(score => score.scoreTypeId === SCORE_TYPE_NAME_FINAL_ID);
    currentScore.comments = comment;
    currentScore.isDirty = true;
  }

  const _reconciliationPrograms = structuredClone(reconciliationPrograms);
  const targetProgram = _reconciliationPrograms.find(p => p.id === targetResult.programId);
  const targetProgramResultType = targetProgram.resultTypes.find(rt => rt.id === targetResult.resultTypeId);
  const targetProgramResult = targetProgramResultType.results.find(r=>r.id === targetResult.id);
  Object.assign(targetProgramResult, targetResult);
  return { updatedResultsInfo, _reconciliationPrograms };
};

export const getTeamScoresToSave = (resultsInfo, departmentReconciliation) => {
  if (departmentReconciliation) {
    return Array.from(resultsInfo.values())
      .flat()
      .flatMap((result) =>
        result.scores
          .filter((score) => score.isDirty)
          .map((score) => ({
            ...score,
            resultId: result.id,
            programId: result.programId,
          })),
      );
  }
  return Array.from(resultsInfo.values())
    .flat()
    .flatMap((result) =>
      result.programs.scores
        .filter((score) => score.isDirty)
        .map((score) => ({
          ...score,
          resultId: result.id,
          programId: result.programs.id,
        })),
    );
};

export const processDepartmentReconciliationData = (data) => {
  if (Object.keys(data).length) {
    let allResults = [];
    const serviceType = data.serviceType.name;
    const actualResultTypes = data.resultTypes.filter(rt=>rt.name === serviceType || rt.name === 'BPA');
    actualResultTypes.forEach(rt=>{
      rt.results.map(r => {
        if (programHasDeptAndFinalScoreForResult(r)) { 
          r.programId = data.id;
          allResults.push(r);
        }
      });
    });

    const allResultsWithScoreOption = allResults.map(result=> {
      return {
        ...result,
        scores: result.scores.map(score => {
          if (score.scoreTypeId !== SCORE_TYPE_NAME_FINAL_ID) {
            return {...score, scoreOption: result.scoreOptions.find(option =>score.scoreTypeId === SCORE_TYPE_NAME_DEPARTMENT_ID && option.id === score.scoreOptionId)};
          } 
          return {
            ...score,
            scoreOption: result.scoreOptions.find(option => option.id === score.scoreOptionId),
            originalScoreOption: result.scores.find(s => s.scoreTypeId === SCORE_TYPE_NAME_FINAL_ID)?.originalScoreOption 
              ? result.scores.find(s => s.scoreTypeId === SCORE_TYPE_NAME_FINAL_ID)?.originalScoreOption 
              : result.scoreOptions.find(option => option.id === score.scoreOptionId),
          };
        })
      };
    });

    return allResultsWithScoreOption;
  }
};

const programHasDeptAndFinalScoreForResult = (programResult) => {
  const deptScore = programResult.scores.find(score => score.scoreTypeId === SCORE_TYPE_NAME_DEPARTMENT_ID);
  const finalScore = programResult.scores.find(score => score.scoreTypeId === SCORE_TYPE_NAME_FINAL_ID);

  return deptScore && finalScore;
};

export const updateDepartmentResultsInfo = (departmentResultsInfo, scoresToSave) => {
  const _departmentResultsInfo = structuredClone(departmentResultsInfo);
  const updatedResultsInfo = _departmentResultsInfo.map(result => {
    return {
      ...result,
      scores: result.scores.map(score => {
        return {
          ...score,
          ...(scoresToSave.find(s => result.id === s.resultId) && { scoreOption: scoresToSave.find(s => result.id === s.resultId).scoreOption }),
          ...(scoresToSave.find(s => result.id === s.resultId) && { originalScoreOption: scoresToSave.find(s => result.id === s.resultId).scoreOption })
        };
      })
    };
  });
  return updatedResultsInfo;
};
